import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoBack = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <style>{`
            @keyframes float {
              0% { transform: translateY(0px); }
              50% { transform: translateY(-8px); }
              100% { transform: translateY(0px); }
            }
            .error-card {
              background: rgba(22, 22, 33, 0.8);
              border: 1px solid rgba(255, 255, 255, 0.08);
              border-radius: 24px;
              padding: 40px;
              max-width: 480px;
              width: 100%;
              text-align: center;
              backdrop-filter: blur(20px);
              -webkit-backdrop-filter: blur(20px);
              box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
              animation: float 5s ease-in-out infinite;
            }
            .reload-btn {
              background: linear-gradient(135deg, #ff3366, #ff6633);
              color: white;
              border: none;
              padding: 12px 28px;
              font-size: 15px;
              font-weight: 600;
              border-radius: 30px;
              cursor: pointer;
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              box-shadow: 0 4px 15px rgba(255, 51, 102, 0.3);
              margin-right: 12px;
              font-family: inherit;
            }
            .reload-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(255, 51, 102, 0.5);
              filter: brightness(1.1);
            }
            .reload-btn:active {
              transform: translateY(0);
            }
            .home-btn {
              background: rgba(255, 255, 255, 0.06);
              color: rgba(255, 255, 255, 0.85);
              border: 1px solid rgba(255, 255, 255, 0.1);
              padding: 12px 28px;
              font-size: 15px;
              font-weight: 600;
              border-radius: 30px;
              cursor: pointer;
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              font-family: inherit;
            }
            .home-btn:hover {
              background: rgba(255, 255, 255, 0.12);
              color: #fff;
              border-color: rgba(255, 255, 255, 0.25);
            }
            .error-details {
              margin-top: 25px;
              text-align: left;
              background: rgba(0, 0, 0, 0.3);
              padding: 16px;
              border-radius: 12px;
              font-family: 'Fira Code', 'Courier New', Courier, monospace;
              font-size: 12px;
              color: #ff5577;
              overflow-x: auto;
              max-height: 180px;
              border: 1px solid rgba(255, 85, 119, 0.15);
              line-height: 1.5;
            }
          `}</style>
          <div className="error-card">
            <div style={styles.iconContainer}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#ff3366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8V12" stroke="#ff3366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16H12.01" stroke="#ff3366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 style={styles.heading}>Something Went Wrong</h2>
            <p style={styles.subheading}>
              An unexpected rendering error occurred in this module. The rest of the platform is running normally.
            </p>
            <div style={styles.buttonGroup}>
              <button className="reload-btn" onClick={this.handleReload}>
                Refresh Page
              </button>
              <button className="home-btn" onClick={this.handleGoBack}>
                Go to Home
              </button>
            </div>
            {this.state.error && (
              <div className="error-details">
                <strong>{this.state.error.toString()}</strong>
                <br />
                {this.state.errorInfo?.componentStack}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: '450px',
    background: 'radial-gradient(circle at center, #161622 0%, #0a0a0f 100%)',
    color: '#fff',
    fontFamily: "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    padding: '24px',
    boxSizing: 'border-box',
  },
  iconContainer: {
    marginBottom: '24px',
    display: 'inline-block',
  },
  heading: {
    fontSize: '26px',
    fontWeight: '700',
    margin: '0 0 12px 0',
    background: 'linear-gradient(135deg, #ffffff 40%, #a5a5b5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
  },
  subheading: {
    fontSize: '14px',
    color: '#a5a5b5',
    lineHeight: '1.6',
    margin: '0 0 32px 0',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }
};

export default ErrorBoundary;
